import { useTranslation } from 'react-i18next';
import { fmtDate } from '../lib/dateFormat';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Calendar as CalendarComponent } from './ui/calendar';
import { toast } from 'sonner';
import { Calendar, Save, Download, Plus, Trash2, CalendarIcon, Filter, Copy, SkipBack, SkipForward, Home, Send, CheckCircle, XCircle, RotateCcw, FileSpreadsheet } from 'lucide-react';
import { format } from 'date-fns';
import axios from 'axios';
import TimesheetPrintDialog from './TimesheetPrintDialog';
import FloatingActions from './FloatingActions';
import { exportView } from '../lib/exportXlsx';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { useAuth } from '../contexts/AuthContext';
import useDeepShortcut from '../hooks/useDeepShortcut';
import usePermissions from '../hooks/usePermissions';
import { CopyNumberButton } from './shared/CopyNumberButton';
import { TimesheetRowFilters } from './TimesheetRowFilters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WeeklyTimesheet = React.memo(({ user }) => {
  const { t } = useTranslation();
  const { dataVersion } = useAuth();
  // Helper: format a JS Date to YYYY-MM-DD using LOCAL components.
  // Avoids the classic `toISOString().split('T')[0]` bug where evening
  // local time gets pushed into the next day in UTC, making the saved
  // currentWeek string drift one day off the displayed label.
  const toLocalISODate = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };
  // Helper functions for week calculations
  const getMondayOfWeek = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1); // adjust when day is Sunday
    return new Date(d.setDate(diff));
  };

  const getWeekNumber = (date) => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  };

  const generateWeekOptions = () => {
    const options = [];
    const today = new Date();
    const currentMonday = getMondayOfWeek(today);
    
    // Generate 26 weeks (6 months back, 6 months forward) from current Monday
    for (let i = -26; i <= 26; i++) {
      const monday = new Date(currentMonday);
      monday.setDate(currentMonday.getDate() + (i * 7));
      
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      
      const weekNum = getWeekNumber(monday);
      const year = monday.getFullYear();
      
      options.push({
        value: toLocalISODate(monday),
        label: `W${weekNum} · ${year} (${fmtDate(monday)} – ${fmtDate(sunday)})`,
        weekNumber: weekNum,
        year: year,
        startDate: monday,
        endDate: sunday
      });
    }
    
    return options.sort((a, b) => new Date(b.value) - new Date(a.value));
  };

  const [currentWeek, setCurrentWeek] = useState('');
  const [timesheet, setTimesheet] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [projects, setProjects] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [weekOptions, setWeekOptions] = useState(generateWeekOptions());
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [weeksWithData, setWeeksWithData] = useState([]);  // List of weeks that have timesheet entries
  
  // Status management state
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [statusAction, setStatusAction] = useState(null); // 'submit', 'approve', 'reject', 'reopen'
  const [statusComment, setStatusComment] = useState('');
  const [statusUpdating, setStatusUpdating] = useState(false);

  // Check if user is admin or manager
  const { can } = usePermissions();
  // Approving/reverting someone's week is a matrix permission, not a job title.
  const isAdminOrManager = can('timesheets.edit');

  // Helper function to create a week option object
  const createWeekOption = useCallback((mondayDate) => {
    // Accept both a Date and a 'YYYY-MM-DD' string; strings are parsed
    // from local components to avoid the UTC-midnight day shift.
    const monday = typeof mondayDate === 'string'
      ? (() => { const [y, m, d] = mondayDate.split('-').map(Number); return new Date(y, m - 1, d); })()
      : new Date(mondayDate);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    const weekNum = getWeekNumber(monday);
    const year = monday.getFullYear();
    
    return {
      value: toLocalISODate(monday),
      label: `W${weekNum} · ${year} (${fmtDate(monday)} – ${fmtDate(sunday)})`,
      weekNumber: weekNum,
      year: year,
      startDate: monday,
      endDate: sunday
    };
  }, []);

  // Ensure the selected week is in the dropdown options
  const ensureWeekInOptions = useCallback((mondayString) => {
    if (!mondayString) return;
    
    setWeekOptions(prevOptions => {
      const exists = prevOptions.some(opt => opt.value === mondayString);
      if (exists) return prevOptions;
      const newOption = createWeekOption(mondayString);
      return [...prevOptions, newOption].sort((a, b) => new Date(b.value) - new Date(a.value));
    });
  }, [createWeekOption]);

  const daysOfWeek = useMemo(() => ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'], []);
  const dayLabels = daysOfWeek.map((d) => t(`timesheetFilters.days.${d}`));
  const dayDates = useMemo(() => {
    if (!currentWeek) return [];
    // Parse YYYY-MM-DD as local date components (avoids any timezone
    // shift from `new Date('YYYY-MM-DDTHH:MM:SS')` parsing).
    const [y, m, dd] = currentWeek.split('-').map(Number);
    return dayLabels.map((_, i) => {
      const d = new Date(y, m - 1, dd + i);  // setDate() auto-handles month rollover
      return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}`;
    });
  }, [currentWeek]);

  useEffect(() => {
    // Set current week to this Monday
    const today = new Date();
    const monday = getMondayOfWeek(today);
    const mondayString = toLocalISODate(monday);
    
    setCurrentWeek(mondayString);
    setSelectedDate(monday);
    
    fetchInitialData();
  }, []);

  // Re-fetch employees/projects/customers when any of them are mutated elsewhere
  useEffect(() => {
    if (dataVersion === 0) return;
    fetchInitialData();
  }, [dataVersion]);

  const fetchWeeklyTimesheet = useCallback(async () => {
    if (!currentWeek) return;
    
    setLoading(true);
    
    try {
      const response = await axios.get(`${API}/weekly-timesheets/${currentWeek}`);
      setTimesheet(response.data);
    } catch (error) {
      console.error('Error fetching weekly timesheet:', error);
      toast.error(t('auto.proj.failedToFetchTimesheet'));
    } finally {
      setLoading(false);
    }
  }, [currentWeek]);

  useEffect(() => {
    if (currentWeek) {
      // Clear current timesheet when changing weeks
      setTimesheet(null);
      fetchWeeklyTimesheet();
    }
  }, [currentWeek, fetchWeeklyTimesheet]);

  const fetchInitialData = async () => {
    try {
      const [employeesRes, projectsRes, customersRes] = await Promise.all([
        axios.get(`${API}/employees`),
        axios.get(`${API}/projects`),
        axios.get(`${API}/customers`)
      ]);
      
      setEmployees(employeesRes.data);
      setProjects(projectsRes.data);
      setCustomers(customersRes.data);
    } catch (error) {
      console.error('Error fetching initial data:', error);
      toast.error(t('auto.proj.failedToFetchData'));
    }
    // Note: don't set loading to false here, let fetchWeeklyTimesheet handle it
  };

  // Fetch all weeks that have timesheet entries
  const fetchWeeksWithData = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/timesheets/weeks-with-entries`);
      const weeks = response.data.weeks || [];
      setWeeksWithData(weeks);
    } catch (error) {
      console.error('Error fetching weeks with data:', error);
    }
  }, []);

  // Fetch weeks with data on mount and after saving
  useEffect(() => {
    fetchWeeksWithData();
  }, [fetchWeeksWithData]);

  // Go to current week
  const goToCurrentWeek = () => {
    const today = new Date();
    const monday = getMondayOfWeek(today);
    const mondayString = toLocalISODate(monday);
    ensureWeekInOptions(mondayString);
    setCurrentWeek(mondayString);
    setSelectedDate(monday);
  };

  // Jump to the closest EARLIER week that has at least one timesheet
  // entry (from any employee visible to this user). Falls back to a
  // simple −7 days if the API list hasn't loaded yet or if we're
  // already past the earliest populated week.
  const goToPreviousWeekWithData = () => {
    if (!currentWeek) return;
    const earlier = (weeksWithData || [])
      .filter((w) => w < currentWeek)
      .sort();               // ascending
    const target = earlier.length ? earlier[earlier.length - 1] : null;
    if (!target) {
      toast.info(t('auto.proj.noEarlierPopulatedWeekFound'));
      return;
    }
    const [y, m, d] = target.split('-').map(Number);
    const targetDate = new Date(y, m - 1, d);
    ensureWeekInOptions(target);
    setCurrentWeek(target);
    setSelectedDate(targetDate);
  };

  // Jump to the closest LATER week that has at least one entry.
  const goToNextWeekWithData = () => {
    if (!currentWeek) return;
    const later = (weeksWithData || [])
      .filter((w) => w > currentWeek)
      .sort();               // ascending
    const target = later.length ? later[0] : null;
    if (!target) {
      toast.info(t('auto.proj.noLaterPopulatedWeekFound'));
      return;
    }
    const [y, m, d] = target.split('-').map(Number);
    const targetDate = new Date(y, m - 1, d);
    ensureWeekInOptions(target);
    setCurrentWeek(target);
    setSelectedDate(targetDate);
  };

  // Quick-action shortcut: "New timesheet row" lands here and adds the row.
  useDeepShortcut({
    active: false,
    tab: 'weekly-timesheets',
    context: { create: 'timesheet' },
    label: '',
    focusKey: 'create',
    onFocus: (v) => { if (v === 'timesheet') addEntry(); },
  });

  const addEntry = () => {
    // Show the new row even if the view was filtered.
    setTsEmployeeFilter('all'); setTsProjectFilter('all');
    if (!employees.length || !projects.length) {
      toast.error(t('auto.proj.pleaseEnsureEmployeesAndProjectsAre'));
      return;
    }

    const newEntry = {
      employee_id: employees[0]?.id || '',
      project_id: projects[0]?.id || '',
      monday: 0,
      tuesday: 0,
      wednesday: 0,
      thursday: 0,
      friday: 0,
      saturday: 0,
      sunday: 0,
      notes: ''
    };

    setTimesheet(prev => ({
      ...prev,
      entries: [...(prev?.entries || []), newEntry]
    }));
  };

  const removeEntry = (index) => {
    setTimesheet(prev => ({
      ...prev,
      entries: prev.entries.filter((_, i) => i !== index)
    }));
  };

  const duplicateEntry = (index) => {
    const entryToCopy = timesheet.entries[index];
    // Create a deep copy of the entry
    const newEntry = {
      employee_id: entryToCopy.employee_id,
      project_id: entryToCopy.project_id,
      monday: entryToCopy.monday || 0,
      tuesday: entryToCopy.tuesday || 0,
      wednesday: entryToCopy.wednesday || 0,
      thursday: entryToCopy.thursday || 0,
      friday: entryToCopy.friday || 0,
      saturday: entryToCopy.saturday || 0,
      sunday: entryToCopy.sunday || 0,
      notes: entryToCopy.notes || ''
    };
    
    setTimesheet(prev => ({
      ...prev,
      entries: [...prev.entries, newEntry]
    }));
    
    toast.success(t('auto.proj.entryDuplicatedSuccessfully'));
  };

  // Throttled update to prevent resize observer loops
  const updateEntryThrottled = useCallback((index, field, value) => {
    // Use requestAnimationFrame to throttle updates
    if (window.updateEntryTimer) {
      cancelAnimationFrame(window.updateEntryTimer);
    }
    
    window.updateEntryTimer = requestAnimationFrame(() => {
      setTimesheet(prev => ({
        ...prev,
        entries: prev.entries.map((entry, i) => 
          i === index ? { ...entry, [field]: value } : entry
        )
      }));
    });
  }, []);

  const updateEntry = updateEntryThrottled;

  // Memoize row calculations to prevent unnecessary recalculations
  const memoizedRowTotals = useMemo(() => {
    if (!timesheet || !timesheet.entries) {
      return [];
    }
    return timesheet.entries.map(entry => 
      daysOfWeek.reduce((total, day) => total + (parseFloat(entry[day]) || 0), 0)
    );
  }, [timesheet, daysOfWeek]);

  const calculateRowTotal = (entry) => {
    return daysOfWeek.reduce((total, day) => total + (parseFloat(entry[day]) || 0), 0);
  };

  // View-only filters (not remembered): totals follow the rows that are shown.
  const [tsEmployeeFilter, setTsEmployeeFilter] = useState('all');
  const [tsProjectFilter, setTsProjectFilter] = useState('all');
  const rowShown = (entry) => (tsEmployeeFilter === 'all' || entry.employee_id === tsEmployeeFilter)
    && (tsProjectFilter === 'all' || entry.project_id === tsProjectFilter);
  const shownEntries = (timesheet?.entries || []).filter(rowShown);
  // A new week has different rows, so start it unfiltered.
  useEffect(() => { setTsEmployeeFilter('all'); setTsProjectFilter('all'); }, [currentWeek]);

  const calculateColumnTotal = (day) => {
    return shownEntries.reduce((total, entry) => total + (parseFloat(entry[day]) || 0), 0);
  };

  const calculateGrandTotal = () => {
    return shownEntries.reduce((total, entry) => total + calculateRowTotal(entry), 0);
  };

  const exportWeekToExcel = () => {
    const TOTAL = '__total__';
    const nameOf = (id) => employees.find((e) => e.id === id)?.name || '';
    const projectOf = (id) => {
      const p = projects.find((x) => x.id === id);
      if (!p) return '';
      return p.project_number ? `${p.project_number} — ${p.name}` : p.name;
    };
    const rows = timesheet.entries.map((entry, i) => ({ ...entry, _total: memoizedRowTotals[i] || 0 }));
    rows.push({
      employee_id: TOTAL, project_id: '',
      _total: (timesheet?.entries || []).reduce((s, e) => s + calculateRowTotal(e), 0),
      ...Object.fromEntries(daysOfWeek.map((d) => [d, (timesheet?.entries || []).reduce((s, e) => s + (parseFloat(e[d]) || 0), 0)])),
    });

    exportView({
      filename: `timesheet-${currentWeek}`, sheetName: 'Timesheet', rows,
      columns: [
        { header: t('auto.lbl.employee'), value: (r) => (r.employee_id === TOTAL ? 'TOTAL' : nameOf(r.employee_id)) },
        { header: t('auto.lbl.project'), value: (r) => (r.employee_id === TOTAL ? '' : projectOf(r.project_id)) },
        ...daysOfWeek.map((day, i) => ({
          header: dayDates[i] ? `${dayLabels[i]} ${dayDates[i]}` : dayLabels[i],
          value: (r) => parseFloat(r[day]) || 0,
        })),
        { header: t('auto.lbl.total'), value: (r) => r._total },
      ],
    });
  };

  const saveTimesheet = async () => {
    if (!timesheet) return;
    
    setSaving(true);
    try {
      if (timesheet.id) {
        // Update existing timesheet
        await axios.put(`${API}/weekly-timesheets/${timesheet.id}`, {
          entries: timesheet.entries
        });
        toast.success(t('auto.proj.timesheetUpdatedSuccessfully'));
      } else {
        // Create new timesheet
        await axios.post(`${API}/weekly-timesheets`, {
          week_start_date: currentWeek,
          entries: timesheet.entries
        });
        toast.success(t('auto.proj.timesheetCreatedSuccessfully'));
        fetchWeeklyTimesheet(); // Refresh to get the ID
      }
      // Refresh the list of weeks with data
      fetchWeeksWithData();
    } catch (error) {
      console.error('Error saving timesheet:', error);
      toast.error(t('auto.proj.failedToSaveTimesheet'));
    } finally {
      setSaving(false);
    }
  };

  // Open status change dialog
  const openStatusDialog = (action) => {
    setStatusAction(action);
    setStatusComment('');
    setStatusDialogOpen(true);
  };

  // Handle status change
  const handleStatusChange = async () => {
    if (!timesheet?.id) {
      toast.error(t('auto.proj.pleaseSaveTheTimesheetFirst'));
      return;
    }

    setStatusUpdating(true);
    
    let newStatus;
    let successMessage;
    
    switch (statusAction) {
      case 'submit':
        newStatus = 'submitted';
        successMessage = 'Timesheet submitted for approval';
        break;
      case 'approve':
        newStatus = 'approved';
        successMessage = 'Timesheet approved';
        break;
      case 'reject':
        newStatus = 'draft';
        successMessage = 'Timesheet rejected and returned to draft';
        break;
      case 'reopen':
        newStatus = 'draft';
        successMessage = 'Timesheet reopened as draft';
        break;
      default:
        setStatusUpdating(false);
        return;
    }

    try {
      await axios.put(`${API}/weekly-timesheets/${timesheet.id}`, {
        status: newStatus
      });
      
      // Update local state
      setTimesheet(prev => ({
        ...prev,
        status: newStatus
      }));
      
      toast.success(successMessage);
      setStatusDialogOpen(false);
    } catch (error) {
      console.error('Error updating timesheet status:', error);
      toast.error(t('auto.proj.failedToUpdateTimesheetStatus'));
    } finally {
      setStatusUpdating(false);
    }
  };

  // Get status badge variant and color
  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return { variant: 'default', className: 'bg-green-600 hover:bg-green-700' };
      case 'submitted':
        return { variant: 'default', className: 'bg-amber-500 hover:bg-amber-600' };
      case 'draft':
      default:
        return { variant: 'secondary', className: '' };
    }
  };

  // Check if timesheet can be edited
  const canEditTimesheet = () => {
    if (!timesheet) return true;
    if (isAdminOrManager) return true; // Admins/managers can always edit
    return timesheet.status === 'draft'; // Regular users can only edit drafts
  };

  const downloadPDF = async () => {
    if (!timesheet?.id) {
      toast.error(t('auto.proj.pleaseSaveTheTimesheetFirst'));
      return;
    }

    // Open print dialog for filtering options
    setPrintDialogOpen(true);
  };

  const navigateWeek = (direction) => {
    // Parse the YYYY-MM-DD week string from LOCAL components. Using
    // `new Date(currentWeek)` parses it as UTC midnight, which in any
    // timezone behind UTC lands on the previous day — making "Next" a
    // no-op and "Prev" jump two weeks.
    const [yy, mm, dd] = (currentWeek || '').split('-').map(Number);
    if (!yy) return;
    const newDate = new Date(yy, mm - 1, dd + (direction * 7));
    const monday = getMondayOfWeek(newDate);
    const mondayString = toLocalISODate(monday);
    
    // Ensure this week exists in the dropdown options
    ensureWeekInOptions(mondayString);
    
    setCurrentWeek(mondayString);
    setSelectedDate(monday);
  };

  const handleDateSelect = (date) => {
    if (date) {
      const monday = getMondayOfWeek(date);
      const mondayString = toLocalISODate(monday);
      
      // Ensure this week exists in the dropdown options
      ensureWeekInOptions(mondayString);
      
      setCurrentWeek(mondayString);
      setSelectedDate(monday);
      setDatePickerOpen(false);
    }
  };

  const handleWeekDropdownChange = (weekValue) => {
    const [yy, mm, dd] = weekValue.split('-').map(Number);
    const monday = new Date(yy, mm - 1, dd);
    setCurrentWeek(weekValue);
    setSelectedDate(monday);
  };

  const getWeekRange = () => {
    if (!currentWeek) return '';
    // Parse local components — avoids UTC-midnight TZ shift
    const [yy, mm, dd] = currentWeek.split('-').map(Number);
    const startDate = new Date(yy, mm - 1, dd);
    const endDate = new Date(yy, mm - 1, dd + 6);
    const weekNum = getWeekNumber(startDate);
    const year = startDate.getFullYear();
    
    return `W${weekNum} · ${year} (${fmtDate(startDate)} – ${fmtDate(endDate)})`;
  };

  const getEmployeeName = (employeeId) => {
    const employee = employees.find(emp => emp.id === employeeId);
    return employee?.name || employee?.employee_code || 'Unknown';
  };

  const getProjectName = (projectId) => {
    const project = projects.find(proj => proj.id === projectId);
    return project?.name || 'Unknown Project';
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!timesheet) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center py-8">
            <p className="text-gray-500">{t('auto.proj.unableToLoadTimesheetData')}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">{t('auto.proj.weeklyTimesheet')}</h2>
          <p className="text-slate-600">{t('weekFilter.trackHoursFor')} {getWeekRange()}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Quick Navigation Buttons */}
          <Button 
            variant="outline" 
            size="sm" 
            onClick={goToPreviousWeekWithData}
            title={t('auto.proj.jumpToPreviousPopulatedWeek')}
           data-testid="btn-gotopreviousweekwithdata">
            <SkipBack className="h-4 w-4" />
          </Button>
          
          <Button variant="outline" size="sm" onClick={() => navigateWeek(-1)} title={t('auto.proj.previousWeek')}>
            ← {t('weekFilter.prev')}
          </Button>
          
          {/* Week Dropdown Selector */}
          <Select value={currentWeek} onValueChange={handleWeekDropdownChange}>
            <SelectTrigger className="w-full min-w-0 sm:w-[330px]">
              <SelectValue placeholder={t('auto.proj.selectAWeek')} />
            </SelectTrigger>
            <SelectContent className="max-h-[300px]">
              {weekOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Date Picker */}
          <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm">
                <CalendarIcon className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">{t('auto.proj.pickDate')}</span>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <CalendarComponent
                mode="single"
                selected={selectedDate}
                onSelect={handleDateSelect}
                initialFocus
              />
            </PopoverContent>
          </Popover>

          <Button variant="outline" size="sm" onClick={() => navigateWeek(1)} title={t('auto.proj.nextWeek')}>
            {t('weekFilter.next')} →
          </Button>
          
          <Button 
            variant="outline" 
            size="sm" 
            onClick={goToNextWeekWithData}
            title={t('auto.proj.jumpToNextPopulatedWeek')}
           data-testid="btn-gotonextweekwithdata">
            <SkipForward className="h-4 w-4" />
          </Button>
          
          <Button 
            variant="outline" 
            size="sm" 
            onClick={goToCurrentWeek}
            title={t('auto.proj.goToCurrentWeek')}
           data-testid="btn-gotocurrentweek">
            <Home className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <CardTitle className="flex items-center space-x-2">
              <Calendar className="h-5 w-5" />
              <span>{getWeekRange()}</span>
            </CardTitle>
            <div className="flex flex-wrap items-center gap-2">
              {/* Status Badge */}
              {timesheet?.status && (
                <Badge className={getStatusBadge(timesheet.status).className}>
                  {timesheet.status === 'approved' && <CheckCircle className="h-3 w-3 mr-1" />}
                  {timesheet.status === 'submitted' && <Send className="h-3 w-3 mr-1" />}
                  {t(`timesheetFilters.status.${timesheet.status}`, { defaultValue: timesheet.status.charAt(0).toUpperCase() + timesheet.status.slice(1) })}
                </Badge>
              )}
              
              {/* Edit Actions - only if timesheet can be edited */}
              {canEditTimesheet() && (
                <>
                  <Button variant="outline" size="sm" onClick={addEntry} data-testid="btn-addentry">
                    <Plus className="h-4 w-4 mr-2" />
                    {t('auto.proj.addEntry')}
                  </Button>
                  <Button onClick={saveTimesheet} disabled={saving} size="sm" data-testid="btn-savetimesheet">
                    <Save className="h-4 w-4 mr-2" />
                    {saving ? t('auto.proj.saving') : t('auto.proj.save')}
                  </Button>
                </>
              )}
              
              {/* Status Change Actions */}
              {timesheet?.id && (
                <>
                  {/* Submit button - show for draft timesheets */}
                  {timesheet.status === 'draft' && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => openStatusDialog('submit')}
                      className="border-amber-500 text-amber-600 hover:bg-amber-50"
                    >
                      <Send className="h-4 w-4 mr-2" />
                      {t('auto.proj.submit')}
                    </Button>
                  )}
                  
                  {/* Admin/Manager actions for submitted timesheets */}
                  {isAdminOrManager && timesheet.status === 'submitted' && (
                    <>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => openStatusDialog('approve')}
                        className="border-green-500 text-green-600 hover:bg-green-50"
                      >
                        <CheckCircle className="h-4 w-4 mr-2" />
                        {t('auto.proj.approve')}
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => openStatusDialog('reject')}
                        className="border-red-500 text-red-600 hover:bg-red-50"
                      >
                        <XCircle className="h-4 w-4 mr-2" />
                        {t('auto.proj.reject')}
                      </Button>
                    </>
                  )}
                  
                  {/* Reopen button - for admins on approved timesheets */}
                  {isAdminOrManager && timesheet.status === 'approved' && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => openStatusDialog('reopen')}
                      className="border-gray-500 text-gray-600 hover:bg-gray-50"
                    >
                      <RotateCcw className="h-4 w-4 mr-2" />
                      {t('auto.proj.reopen')}
                    </Button>
                  )}
                  
                  <Button variant="outline" onClick={downloadPDF} size="sm" data-testid="btn-wnloadpdf">
                    <Filter className="h-4 w-4 mr-2" />
                    {t('auto.proj.printOptions')}
                  </Button>
                </>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {!timesheet?.entries?.length ? (
            <div className="text-center py-8">
              <Calendar className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p className="text-gray-500">{t('auto.proj.noTimesheetEntriesForThisWeek')}</p>
              <p className="text-sm text-gray-400">Click &quot;Add Entry&quot; to get started</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <TimesheetRowFilters
                entries={timesheet.entries} employees={employees} projects={projects}
                employee={tsEmployeeFilter} setEmployee={setTsEmployeeFilter}
                project={tsProjectFilter} setProject={setTsProjectFilter} shown={shownEntries.length}
              />
              <table className="w-full border-collapse border border-gray-300">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="border border-gray-300 p-3 text-left font-medium">{t('auto.proj.employee')}</th>
                    <th className="border border-gray-300 p-3 text-left font-medium">{t('auto.proj.project')}</th>
                    {dayLabels.map((day, i) => (
                      <th key={day} className="border border-gray-300 p-2 text-center font-medium min-w-[80px]">
                        <div>{day}</div>
                        {dayDates[i] && <div className="text-xs text-gray-500 font-normal">{dayDates[i]}</div>}
                      </th>
                    ))}
                    <th className="border border-gray-300 p-3 text-center font-medium">{t('auto.proj.total')}</th>
                    <th className="border border-gray-300 p-3 text-center font-medium">{t('auto.proj.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {timesheet.entries.map((entry, index) => rowShown(entry) && (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="border border-gray-300 p-2">
                        <Select
                          value={entry.employee_id}
                          onValueChange={(value) => updateEntry(index, 'employee_id', value)}
                          disabled={!canEditTimesheet()}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder={t('auto.proj.selectEmployee2')} />
                          </SelectTrigger>
                          <SelectContent>
                            {employees.map(employee => (
                              <SelectItem key={employee.id} value={employee.id}>
                                {employee.name || employee.employee_code}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="border border-gray-300 p-2">
                        <Select
                          value={entry.project_id}
                          onValueChange={(value) => updateEntry(index, 'project_id', value)}
                          disabled={!canEditTimesheet()}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder={t('auto.proj.selectProject')} />
                          </SelectTrigger>
                          <SelectContent>
                            {projects.map(project => (
                              <SelectItem key={project.id} value={project.id}>
                                {project.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      {daysOfWeek.map(day => (
                        <td key={day} className="border border-gray-300 p-2">
                          <Input
                            type="number"
                            min="0"
                            max="24"
                            step="0.25"
                            value={entry[day] || ''}
                            onChange={(e) => updateEntry(index, day, parseFloat(e.target.value) || 0)}
                            className="w-full text-center"
                            placeholder="0"
                            disabled={!canEditTimesheet()}
                          />
                        </td>
                      ))}
                      <td className="border border-gray-300 p-3 text-center font-medium bg-gray-50">
                        <span className="inline-flex items-center gap-0.5 whitespace-nowrap">
                          {(memoizedRowTotals[index] || 0).toFixed(2)}
                          <CopyNumberButton value={memoizedRowTotals[index] || 0} trim size="sm" testId={`copy-timesheet-row-total-${index}`} />
                        </span>
                      </td>
                      <td className="border border-gray-300 p-2 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Button
                            variant="outline"
                            size="sm" 
                            onClick={() => duplicateEntry(index)}
                            className="text-blue-600 hover:text-blue-700"
                            title={t('auto.proj.duplicateEntry')}
                            disabled={!canEditTimesheet()}
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm" 
                            onClick={() => removeEntry(index)}
                            className="text-red-600 hover:text-red-700"
                            title={t('auto.proj.deleteEntry')}
                            disabled={!canEditTimesheet()}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-gray-100 font-medium">
                    <td className="border border-gray-300 p-3" colSpan="2">{t('auto.proj.totalHours')}</td>
                    {daysOfWeek.map(day => (
                      <td key={day} className="border border-gray-300 p-3 text-center">
                        <span className="inline-flex items-center gap-0.5 whitespace-nowrap">
                          {calculateColumnTotal(day).toFixed(2)}
                          <CopyNumberButton value={calculateColumnTotal(day)} trim size="sm" testId={`copy-timesheet-day-total-${day}`} />
                        </span>
                      </td>
                    ))}
                    <td className="border border-gray-300 p-3 text-center font-bold">
                      <span className="inline-flex items-center gap-0.5 whitespace-nowrap">
                        {calculateGrandTotal().toFixed(2)}
                        <CopyNumberButton value={calculateGrandTotal()} trim size="sm" testId="copy-timesheet-grand-total" />
                      </span>
                    </td>
                    <td className="border border-gray-300 p-3"></td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <FloatingActions actions={[
        { icon: Save, label: saving ? t('auto.proj.saving2') : t('auto.proj.saveTimesheet'), tone: 'primary',
          testId: 'timesheet-floating-save-btn', hidden: !canEditTimesheet(),
          disabled: saving, onClick: saveTimesheet },
        { icon: CheckCircle, label: t('auto.lbl.approveTimesheet'), tone: 'success',
          testId: 'timesheet-floating-approve-btn',
          hidden: !(isAdminOrManager && timesheet?.status === 'submitted'),
          onClick: () => openStatusDialog('approve') },
        { icon: FileSpreadsheet, label: t('auto.lbl.exportThisWeekToExcel'),
          testId: 'timesheet-floating-excel-btn',
          hidden: !timesheet?.entries?.length, onClick: exportWeekToExcel },
        { icon: Download, label: t('auto.lbl.printExport'), testId: 'timesheet-floating-export-btn',
          onClick: downloadPDF },
      ]} />
      
      {/* Print Dialog */}
      <TimesheetPrintDialog
        open={printDialogOpen}
        onOpenChange={setPrintDialogOpen}
        timesheet={timesheet}
        employees={employees}
        projects={projects}
        customers={customers}
        weekRange={getWeekRange()}
      />

      {/* Status Change Dialog */}
      <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {statusAction === 'submit' && 'Submit Timesheet for Approval'}
              {statusAction === 'approve' && 'Approve Timesheet'}
              {statusAction === 'reject' && 'Reject Timesheet'}
              {statusAction === 'reopen' && 'Reopen Timesheet'}
            </DialogTitle>
            <DialogDescription>
              {statusAction === 'submit' && 'This will submit the timesheet for manager/admin approval. You won\'t be able to edit it until it\'s approved or rejected.'}
              {statusAction === 'approve' && 'This will approve the timesheet and mark it as final.'}
              {statusAction === 'reject' && 'This will reject the timesheet and return it to draft status for corrections.'}
              {statusAction === 'reopen' && 'This will return the timesheet to draft status, allowing edits.'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <div className="p-3 bg-gray-50 rounded-lg mb-4">
              <div className="text-sm text-gray-600">
                <strong>{t('auto.proj.week')}</strong> {getWeekRange()}
              </div>
              <div className="text-sm text-gray-600">
                <strong>{t('auto.proj.totalHours2')}</strong> {timesheet?.entries?.reduce((total, entry) => 
                  total + daysOfWeek.reduce((sum, day) => sum + (parseFloat(entry[day]) || 0), 0), 0
                ).toFixed(2)}
              </div>
              <div className="text-sm text-gray-600">
                <strong>{t('auto.proj.currentStatus')}</strong> {timesheet?.status?.charAt(0).toUpperCase() + timesheet?.status?.slice(1)}
              </div>
            </div>
            
            {(statusAction === 'reject' || statusAction === 'reopen') && (
              <div className="space-y-2">
                <Label htmlFor="statusComment">{t('auto.proj.commentOptional')}</Label>
                <Textarea
                  id="statusComment"
                  value={statusComment}
                  onChange={(e) => setStatusComment(e.target.value)}
                  placeholder={statusAction === 'reject' ? t('auto.proj.reasonForRejection') : t('auto.proj.reasonForReopening')}
                  rows={3}
                />
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusDialogOpen(false)} data-testid="dialog-cancel-btn">
              {t('auto.proj.cancel')}
            </Button>
            <Button 
              onClick={handleStatusChange}
              disabled={statusUpdating}
              className={
                statusAction === 'approve' ? 'bg-green-600 hover:bg-green-700' :
                statusAction === 'reject' ? 'bg-red-600 hover:bg-red-700' :
                statusAction === 'submit' ? 'bg-amber-500 hover:bg-amber-600' :
                ''
              }
             data-testid="dialog-statuschange-btn">
              {statusUpdating ? 'Processing...' : (
                <>
                  {statusAction === 'submit' && <><Send className="h-4 w-4 mr-2" /> {t('auto.proj.submit')}</>}
                  {statusAction === 'approve' && <><CheckCircle className="h-4 w-4 mr-2" /> {t('auto.proj.approve')}</>}
                  {statusAction === 'reject' && <><XCircle className="h-4 w-4 mr-2" /> {t('auto.proj.reject')}</>}
                  {statusAction === 'reopen' && <><RotateCcw className="h-4 w-4 mr-2" /> {t('auto.proj.reopen')}</>}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
});

export default WeeklyTimesheet;